import React from 'react';

import { useTranslation, LTR_TEXT } from '../i18n';

/**
 * Who did this — a Keycloak subject, or the scheduler.
 *
 * The audit trail records `actor: ctx.user.sub`, so what arrives is a raw UUID.
 * Printed whole it is 36 characters of noise that pushes every other column off a
 * dense table and tells an operator nothing; printed as nothing it loses the only
 * accountability the trail has. So: the first eight characters, monospace and
 * LTR-isolated (an id is a Latin run and must read left-to-right inside a Persian
 * row), with the whole value in `title` for the moment somebody genuinely needs to
 * match it against Keycloak.
 *
 * `SYSTEM` is the scheduler — expiry, past-due transitions, renewal reminders —
 * and it says so in words, because «SYSTEM» in an audit column reads like a
 * person's username.
 *
 * When the BFF learns to resolve a display name (it has the Keycloak admin client
 * to do it), pass `name` and this prefers it. Until then the truncated id is the
 * honest rendering: it does not pretend to know who the operator was.
 */
export default function ActorLabel({ actor, name }) {
  const { t } = useTranslation();

  if (!actor) return <span>{t('common.value.none')}</span>;
  if (actor === 'SYSTEM') return <span>{t('common.value.system')}</span>;
  if (name) return <span dir="auto">{name}</span>;

  const short = String(actor).slice(0, 8);
  return (
    <span
      {...LTR_TEXT}
      className="font-mono text-xs text-muted-foreground"
      title={actor}
    >
      {short}…
    </span>
  );
}
